// Iter · Feb 2026 — Monthly Delivery Report.
// A summary pivot of Content Calendar deliverables so a PM can see, at
// a glance, how each client's packages are performing month over month.
//
// Rows      : Client → Package
// Columns   : Month
// Cell      : "done / total · pending" (green when 100%, amber when
//             partial, rose when 0% delivered but items exist)
//
// We call the same `content-items-enriched` endpoint the calendar uses,
// once per month in parallel, then bucket the rows client-side. This
// keeps the backend contract untouched and reuses existing enrichment
// (client_name, package_name, status). "Done" = status `published`;
// items with status `cancelled` are excluded from totals.

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiSelect from "@/components/ui/multi-select";
import { Button } from "@/components/ui/button";
import { RefreshCw, Download, ChevronRight, ChevronDown } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PackageHoverPreview from "@/components/packages/PackageHoverPreview";

const DONE_STATUSES = new Set(["published"]);
const EXCLUDED_STATUSES = new Set(["cancelled"]);

function monthStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}
function shiftMonth(base, delta) {
  const [y, m] = base.split("-").map(Number);
  return monthStr(new Date(y, m - 1 + delta, 1));
}
function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en", { month: "short", year: "2-digit" });
}

function cellTone(done, total) {
  if (total === 0) return "text-muted-foreground";
  if (done >= total) return "text-emerald-700 font-semibold";
  if (done === 0) return "text-rose-600 font-semibold";
  return "text-amber-700";
}

export default function ContentCalendarSummary({ wsId, brands, currentMonth }) {
  const [monthsBack, setMonthsBack] = useState("6");
  const [brandF, setBrandF] = useState([]);
  const [clientF, setClientF] = useState([]);
  const [packageF, setPackageF] = useState([]);
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState([]); // flat list of {month, ...item}
  // Iter · Feb 2026 — Deep expansion. Store expanded package + category
  // keys in Sets so any level can be independently opened/closed.
  const [expandedPkgs, setExpandedPkgs] = useState(() => new Set());
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  const togglePkg = (k) => setExpandedPkgs((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const toggleCat = (k) => setExpandedCats((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  // Compute month list ending at currentMonth, extending backward.
  const monthList = useMemo(() => {
    const n = Number(monthsBack) || 6;
    const arr = [];
    for (let i = n - 1; i >= 0; i--) arr.push(shiftMonth(currentMonth, -i));
    return arr;
  }, [monthsBack, currentMonth]);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        monthList.map(async (m) => {
          try {
            const r = await api.get(`/workspaces/${wsId}/content-items-enriched?month=${m}&limit=5000`);
            const rows = r.data?.rows || r.data || [];
            return rows.map((it) => ({ ...it, __month: m }));
          } catch {
            return [];
          }
        })
      );
      setRaw(results.flat());
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load summary"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, monthsBack, currentMonth]);

  // Derive available client / package options from raw for the filters,
  // so filters only show entities that actually appear in the range.
  const { clientOptions, packageOptions } = useMemo(() => {
    const cs = new Map();
    const ps = new Map();
    for (const it of raw) {
      const cKey = it.client_id || it.client_name || "_none";
      const cName = it.client_name || "— No client —";
      if (!cs.has(cKey)) cs.set(cKey, { value: cKey, label: cName });
      const pKey = it.package_id || it.package_name || "_none";
      const pName = it.package_name || "— No package —";
      if (!ps.has(pKey)) ps.set(pKey, { value: pKey, label: pName });
    }
    return {
      clientOptions: [...cs.values()].sort((a, b) => a.label.localeCompare(b.label)),
      packageOptions: [...ps.values()].sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [raw]);

  // Filter + hierarchical pivot: Client → Package → Category → Item.
  // At every level we roll up per-month {done, total} cells so any row
  // can be presented on its own. Pending task lists are attached at
  // the deliverable level so the hover-card can show "who is stuck on
  // what" without a second fetch.
  const pivot = useMemo(() => {
    const clients = new Map();
    const ensureCell = (mapObj, m) => {
      const c = mapObj[m] || { done: 0, total: 0 };
      mapObj[m] = c;
      return c;
    };
    for (const it of raw) {
      if (EXCLUDED_STATUSES.has(it.status)) continue;
      if (brandF.length && !brandF.includes(it.brand_id)) continue;
      const cKey = it.client_id || it.client_name || "_none";
      const pKey = it.package_id || it.package_name || "_none";
      if (clientF.length && !clientF.includes(cKey)) continue;
      if (packageF.length && !packageF.includes(pKey)) continue;
      const catKey = it.category || "Uncategorised";

      if (!clients.has(cKey)) {
        clients.set(cKey, {
          key: cKey,
          name: it.client_name || "— No client —",
          packages: new Map(),
        });
      }
      const c = clients.get(cKey);
      if (!c.packages.has(pKey)) {
        c.packages.set(pKey, {
          key: pKey,
          name: it.package_name || "— No package —",
          months: {},
          categories: new Map(),
        });
      }
      const p = c.packages.get(pKey);
      if (!p.categories.has(catKey)) {
        p.categories.set(catKey, {
          key: catKey,
          name: catKey,
          color: it.activity_type_color,
          months: {},
          items: [],
        });
      }
      const cat = p.categories.get(catKey);
      cat.items.push(it);
      if (!cat.color && it.activity_type_color) cat.color = it.activity_type_color;

      // Roll up done/total at all three levels.
      const isDone = DONE_STATUSES.has(it.status);
      ensureCell(p.months, it.__month).total += 1;
      if (isDone) ensureCell(p.months, it.__month).done += 1;
      ensureCell(cat.months, it.__month).total += 1;
      if (isDone) ensureCell(cat.months, it.__month).done += 1;
    }
    // Materialise Maps → sorted arrays for render.
    const rows = [...clients.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({
        ...c,
        packages: [...c.packages.values()]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((p) => ({
            ...p,
            categories: [...p.categories.values()]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((cat) => ({
                ...cat,
                items: cat.items.sort((a, b) => (a.planned_date || "").localeCompare(b.planned_date || "")),
              })),
          })),
      }));
    return rows;
  }, [raw, brandF, clientF, packageF]);

  // Column totals (per month, across all filtered rows) + grand total.
  const totals = useMemo(() => {
    const byMonth = {};
    let done = 0, total = 0;
    for (const c of pivot) for (const p of c.packages) for (const m of monthList) {
      const cell = p.months[m];
      if (!cell) continue;
      const t = byMonth[m] || { done: 0, total: 0 };
      t.done += cell.done;
      t.total += cell.total;
      byMonth[m] = t;
      done += cell.done;
      total += cell.total;
    }
    return { byMonth, done, total };
  }, [pivot, monthList]);

  const exportCsv = () => {
    const header = ["Client", "Package", ...monthList.map(monthLabel), "Grand total (done / total)"];
    const rows = [];
    for (const c of pivot) for (const p of c.packages) {
      let rowDone = 0, rowTotal = 0;
      const cells = monthList.map((m) => {
        const cell = p.months[m];
        if (!cell) return "";
        rowDone += cell.done;
        rowTotal += cell.total;
        return `${cell.done}/${cell.total}`;
      });
      rows.push([c.name, p.name, ...cells, `${rowDone}/${rowTotal}`]);
    }
    const totalsRow = [
      "TOTAL", "",
      ...monthList.map((m) => {
        const t = totals.byMonth[m];
        return t ? `${t.done}/${t.total}` : "";
      }),
      `${totals.done}/${totals.total}`,
    ];
    rows.push(totalsRow);
    const csv = [header, ...rows].map((r) => r.map((x) => `"${String(x).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `content-summary-${monthList[0]}_to_${monthList[monthList.length - 1]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper: pending items in a cell (a package/category × month).
  // "Pending" = not published, not cancelled. Returns a compact list
  // suitable for a hover card, each with its own pending task list.
  const pendingBreakdown = (items) => {
    const out = [];
    for (const it of items) {
      if (it.status === "published" || it.status === "cancelled") continue;
      const tasks = (it.tasks || []).filter((t) => t.status !== "done" && t.status !== "cancelled");
      out.push({
        id: it.id,
        name: it.name,
        date: it.planned_date,
        status: it.status,
        tasks,
      });
    }
    return out.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  };

  // Reusable cell renderer: shows done/total plus a pending pill that
  // hover-expands into a task list. `items` scopes the breakdown to the
  // current bucket (package·month or category·month).
  const renderCell = (cell, items, monthKey, testid) => {
    if (!cell) {
      return <td key={monthKey} className="text-center px-2 py-1 text-muted-foreground/50" data-testid={testid}>—</td>;
    }
    const pending = cell.total - cell.done;
    const scopedItems = (items || []).filter((it) => it.__month === monthKey);
    const breakdown = pending > 0 ? pendingBreakdown(scopedItems) : [];
    return (
      <td key={monthKey} className={`text-center px-2 py-1 font-mono ${cellTone(cell.done, cell.total)}`}
          title={`Delivered ${cell.done} of ${cell.total} · ${pending} pending`}
          data-testid={testid}>
        {cell.done}/{cell.total}
        {pending > 0 && (
          <HoverCard openDelay={120} closeDelay={80}>
            <HoverCardTrigger asChild>
              <span className="block text-[9px] text-rose-600 font-normal cursor-help underline decoration-dotted underline-offset-2"
                    data-testid={`${testid}-pending`}>
                {pending} pending
              </span>
            </HoverCardTrigger>
            <HoverCardContent side="right" align="start" className="w-80 p-0 rounded-none max-h-72 overflow-auto"
                              data-testid={`${testid}-pending-hover`}>
              <div className="px-3 py-2 border-b border-border bg-muted/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  {pending} pending in {monthLabel(monthKey)}
                </div>
              </div>
              {breakdown.length === 0 && (
                <div className="px-3 py-3 text-[11px] text-muted-foreground">No detail available.</div>
              )}
              <ul className="divide-y divide-border">
                {breakdown.map((b) => (
                  <li key={b.id} className="px-3 py-2 text-[11px]">
                    <div className="font-medium text-foreground truncate flex items-center gap-1.5">
                      <span className="font-mono text-[9px] text-muted-foreground">{(b.date || "").slice(5)}</span>
                      <span className="truncate">{b.name}</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-amber-700 mt-0.5">{b.status.replaceAll("_", " ")}</div>
                    {b.tasks.length > 0 ? (
                      <ul className="mt-1 space-y-0.5">
                        {b.tasks.slice(0, 6).map((t) => (
                          <li key={t.id} className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                            <span className="truncate flex-1">{t.title}</span>
                            {t.assignee_name && <span className="text-[9px] shrink-0">· {t.assignee_name}</span>}
                            {t.due_date && <span className="text-[9px] font-mono shrink-0">· {t.due_date.slice(5)}</span>}
                          </li>
                        ))}
                        {b.tasks.length > 6 && (
                          <li className="text-[9px] text-muted-foreground">+ {b.tasks.length - 6} more tasks</li>
                        )}
                      </ul>
                    ) : (
                      <div className="text-[10px] text-muted-foreground italic mt-0.5">No open tasks.</div>
                    )}
                  </li>
                ))}
              </ul>
            </HoverCardContent>
          </HoverCard>
        )}
      </td>
    );
  };

  // Compute total visible rows per client (for the client-cell rowSpan)
  // taking package/category expansion into account.
  const rowCounts = useMemo(() => {
    const m = new Map();
    for (const c of pivot) {
      let n = 0;
      for (const p of c.packages) {
        n += 1;
        if (expandedPkgs.has(p.key)) {
          for (const cat of p.categories) {
            n += 1;
            if (expandedCats.has(`${p.key}/${cat.key}`)) n += cat.items.length;
          }
        }
      }
      m.set(c.key, n || 1);
    }
    return m;
  }, [pivot, expandedPkgs, expandedCats]);

  return (
    <div className="space-y-3" data-testid="cc-summary">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap border border-border bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Range</span>
          <Select value={monthsBack} onValueChange={setMonthsBack}>
            <SelectTrigger className="h-7 w-[130px] rounded-none text-[11px] px-2" data-testid="cc-summary-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MultiSelect
          options={(brands || []).map((b) => ({ value: b.id, label: b.name }))}
          value={brandF}
          onChange={setBrandF}
          allLabel="All brands"
          placeholder="Brand"
          width="w-44"
          testid="cc-summary-brand"
        />
        <MultiSelect
          options={clientOptions}
          value={clientF}
          onChange={setClientF}
          allLabel="All clients"
          placeholder="Client"
          width="w-44"
          testid="cc-summary-client"
        />
        <MultiSelect
          options={packageOptions}
          value={packageF}
          onChange={setPackageF}
          allLabel="All packages"
          placeholder="Package"
          width="w-44"
          testid="cc-summary-package"
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 rounded-none text-[11px]"
          onClick={load}
          data-testid="cc-summary-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 rounded-none text-[11px]"
          onClick={exportCsv}
          disabled={pivot.length === 0}
          data-testid="cc-summary-export"
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>
        <span className="text-[10px] text-muted-foreground ml-auto" data-testid="cc-summary-count">
          {totals.done} of {totals.total} delivered ({totals.total ? Math.round((totals.done / totals.total) * 100) : 0}%)
        </span>
      </div>

      {/* Pivot table */}
      <div className="border border-border overflow-x-auto">
        <table className="min-w-full text-[11px] border-collapse" data-testid="cc-summary-table">
          <thead className="bg-muted/40 sticky top-0 z-[5]">
            <tr>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-[220px]">Client</th>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-[260px]">Package</th>
              {monthList.map((m) => (
                <th key={m} className="text-center px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap">
                  {monthLabel(m)}
                </th>
              ))}
              <th className="text-center px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap bg-muted/60">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={monthList.length + 3} className="text-center px-3 py-6 text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && pivot.length === 0 && (
              <tr><td colSpan={monthList.length + 3} className="text-center px-3 py-6 text-muted-foreground">No deliverables in this range.</td></tr>
            )}
            {!loading && pivot.map((c) => {
              const clientRowSpan = rowCounts.get(c.key) || 1;
              let clientCellRendered = false;
              const renderClientCell = () => {
                if (clientCellRendered) return null;
                clientCellRendered = true;
                return (
                  <td rowSpan={clientRowSpan}
                      className="px-3 py-1 align-top font-medium text-indigo-700 border-r border-border/60"
                      data-testid={`cc-summary-client-${c.key}`}>
                    {c.name}
                    <div className="text-[10px] text-muted-foreground font-normal">
                      {c.packages.length} package{c.packages.length === 1 ? "" : "s"}
                    </div>
                  </td>
                );
              };
              return (
                <React.Fragment key={c.key}>
                  {c.packages.map((p) => {
                    const pExpanded = expandedPkgs.has(p.key);
                    let rowDone = 0, rowTotal = 0;
                    const pkgItems = p.categories.flatMap((cat) => cat.items);
                    return (
                      <React.Fragment key={`${c.key}-${p.key}`}>
                        <tr className="border-t border-border/60 hover:bg-muted/20"
                            data-testid={`cc-summary-row-${p.key}`}>
                          {renderClientCell()}
                          <td className="px-3 py-1 border-r border-border/60">
                            <button className="inline-flex items-center gap-1.5 text-left hover:text-primary"
                                    onClick={() => togglePkg(p.key)}
                                    data-testid={`cc-summary-pkg-toggle-${p.key}`}>
                              {pExpanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
                              <PackageHoverPreview pid={p.key !== "_none" ? p.key : null} disabled={p.key === "_none"}>
                                <span className="chip text-[10px] bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30 truncate cursor-help">
                                  {p.name}
                                </span>
                              </PackageHoverPreview>
                              <span className="text-[9px] text-muted-foreground">
                                {p.categories.length} cat · {pkgItems.length} items
                              </span>
                            </button>
                          </td>
                          {monthList.map((m) => {
                            const cell = p.months[m];
                            if (cell) { rowDone += cell.done; rowTotal += cell.total; }
                            return renderCell(cell, pkgItems, m, `cc-summary-cell-${p.key}-${m}`);
                          })}
                          <td className={`text-center px-2 py-1 font-mono bg-muted/40 ${cellTone(rowDone, rowTotal)}`}
                              data-testid={`cc-summary-rowtotal-${p.key}`}>
                            {rowDone}/{rowTotal}
                          </td>
                        </tr>
                        {pExpanded && p.categories.map((cat) => {
                          const catKey = `${p.key}/${cat.key}`;
                          const cExpanded = expandedCats.has(catKey);
                          let catDone = 0, catTotal = 0;
                          return (
                            <React.Fragment key={catKey}>
                              <tr className="bg-muted/10 border-t border-border/40 hover:bg-muted/25"
                                  data-testid={`cc-summary-cat-${p.key}-${cat.key}`}>
                                {renderClientCell()}
                                <td className="px-3 py-1 pl-8 border-r border-border/60">
                                  <button className="inline-flex items-center gap-1.5 text-left text-[11px] hover:text-primary"
                                          onClick={() => toggleCat(catKey)}
                                          data-testid={`cc-summary-cat-toggle-${p.key}-${cat.key}`}>
                                    {cExpanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cat.color || "#8b5cf6" }} />
                                    <span className="truncate">{cat.name}</span>
                                    <span className="text-[9px] text-muted-foreground">{cat.items.length} items</span>
                                  </button>
                                </td>
                                {monthList.map((m) => {
                                  const cell = cat.months[m];
                                  if (cell) { catDone += cell.done; catTotal += cell.total; }
                                  return renderCell(cell, cat.items, m, `cc-summary-catcell-${p.key}-${cat.key}-${m}`);
                                })}
                                <td className={`text-center px-2 py-1 font-mono bg-muted/40 ${cellTone(catDone, catTotal)}`}
                                    data-testid={`cc-summary-catrowtotal-${p.key}-${cat.key}`}>
                                  {catDone}/{catTotal}
                                </td>
                              </tr>
                              {cExpanded && cat.items.map((it) => {
                                const done = DONE_STATUSES.has(it.status);
                                const pending = !done;
                                return (
                                  <tr key={it.id} className="bg-muted/5 border-t border-border/30 hover:bg-muted/20"
                                      data-testid={`cc-summary-item-${it.id}`}>
                                    {renderClientCell()}
                                    <td className="px-3 py-1 pl-14 border-r border-border/60 text-[10.5px]">
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-mono text-[9px] text-muted-foreground shrink-0">{(it.planned_date || "").slice(5)}</span>
                                        <span className="truncate">{it.name}</span>
                                      </div>
                                      {/* Feb 2026 · Topic line under the deliverable name so
                                          admins see what each post is about even in the dense
                                          Summary grid. Linked-bucket topics show in green with
                                          the chain icon; flat topics stay muted italic. */}
                                      {(() => {
                                        const topic = it.linked_bucket?.topic_title || it.topic;
                                        if (!topic) return null;
                                        const linked = Boolean(it.linked_bucket?.topic_title);
                                        return (
                                          <div className={`text-[9px] truncate pl-4 ${linked ? "text-emerald-700" : "text-muted-foreground italic"}`}
                                                title={`Topic: ${topic}`}>
                                            {linked && "🔗 "}{topic}
                                          </div>
                                        );
                                      })()}
                                      <div className="text-[9px] text-muted-foreground pl-4">
                                        {it.status.replaceAll("_", " ")} · {(it.tasks || []).filter((t) => t.status !== "done").length} open tasks
                                      </div>
                                    </td>
                                    {monthList.map((m) => {
                                      if (m !== it.__month) {
                                        return <td key={m} className="text-center px-2 py-1 text-muted-foreground/30">·</td>;
                                      }
                                      const cell = { done: done ? 1 : 0, total: 1 };
                                      return renderCell(cell, [it], m, `cc-summary-itemcell-${it.id}-${m}`);
                                    })}
                                    <td className={`text-center px-2 py-1 font-mono bg-muted/30 ${done ? "text-emerald-700 font-semibold" : "text-amber-700"}`}
                                        data-testid={`cc-summary-itemtotal-${it.id}`}>
                                      {done ? "1/1" : `0/1${pending ? " · 1p" : ""}`}
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          {!loading && pivot.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/60 font-semibold">
                <td className="px-3 py-1.5 text-[10px] uppercase tracking-widest" colSpan={2}>Total</td>
                {monthList.map((m) => {
                  const t = totals.byMonth[m] || { done: 0, total: 0 };
                  return (
                    <td key={m} className={`text-center px-2 py-1 font-mono ${cellTone(t.done, t.total)}`}
                        data-testid={`cc-summary-coltotal-${m}`}>
                      {t.done}/{t.total}
                    </td>
                  );
                })}
                <td className={`text-center px-2 py-1 font-mono bg-muted ${cellTone(totals.done, totals.total)}`}
                    data-testid="cc-summary-grandtotal">
                  {totals.done}/{totals.total}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Delivered = <b>published</b>. Cancelled deliverables are excluded from totals.
      </div>
    </div>
  );
}
